import { useSyncExternalStore } from "react";
import { NUMBERED_PINS } from "@majourneys/shared";
import { RADIUS, SOFT } from "./soft-tokens";

/* ------------------------------------------------------------------ */
/* Which result the reader is pointing at (chat kit C5 + C6)           */
/* ------------------------------------------------------------------ */

/**
 * The slug the reader has under the pointer or keyboard focus, on a card or
 * on a pin. Hovering card 3 lifts pin 3, and hovering pin 3 lifts card 3.
 *
 * A tiny store rather than context so the card rail and the map, which the
 * turn draws as siblings in different places, need no shared parent wired
 * up. Keyed by slug, the same key `resultNumbers` uses, so a card and a pin
 * can only agree when they are the same place.
 */
let activeSlug: string | null = null;
const listeners = new Set<() => void>();

export function setActivePin(slug: string | null) {
  if (activeSlug === slug) return;
  activeSlug = slug;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True while this slug is the one being pointed at, on a card or a pin. */
export function useIsActivePin(slug: string | null | undefined): boolean {
  return useSyncExternalStore(
    subscribe,
    () => slug != null && activeSlug === slug,
    () => false,
  );
}

/** Ink on the gold pin: navy on gold fails contrast, this passes. */
const LEAD_INK = "#4a3411";

/**
 * A map pin whose label is the result's position in the answer.
 *
 * Pin 2 is card 2. That is the whole idea: the reference we were shown labels
 * its pins with a Google rating, and we do not do ratings (owner, 2026-09-16),
 * so the number carries the one thing a rating never did — which pin is which
 * card. Twelve medina restaurants sit inside a few hundred metres, and a
 * number stays readable at that density where a name does not.
 *
 * Only the leading {@link NUMBERED_PINS} carry a number; the rest are plain
 * dots. They stay on the map because leaving them off would hide most of a
 * twenty-result answer, which is the failure `shouldShowResultsMap` exists to
 * refuse.
 *
 * The phone's twin is `apps/mobile/src/features/discover/components/chat/
 * number-pin.tsx`. Its own module on both sides because the map and the card
 * badge both draw it.
 */
export function NumberPin({
  index,
  lead = false,
  active = false,
}: {
  index: number;
  lead?: boolean;
  /**
   * Lifted: the reader is pointing at this result's card or pin. The lift is
   * a transform (transition only under motion-safe) and a ring, so reduced
   * motion still sees which pin it is, just without the movement.
   */
  active?: boolean;
}) {
  // Past the leading few, a plain dot. Twenty numbers on a 240px card is a
  // wall of digits, and dropping those pins instead would hide most of the
  // answer. See NUMBERED_PINS.
  //
  // No per-surface override. A card badged "9" whose pin is an unnumbered dot
  // points at nothing the reader can find, which is the whole complaint that
  // produced this rule.
  const lift = active ? activeLift : undefined;
  if (index >= NUMBERED_PINS) {
    return (
      <span
        data-active={active ? "true" : undefined}
        className="motion-safe:transition-transform motion-safe:duration-200"
        style={{ ...dot, ...lift }}
      />
    );
  }
  return (
    <span
      data-active={active ? "true" : undefined}
      className="motion-safe:transition-transform motion-safe:duration-200"
      style={{ ...pin, ...(lead ? pinLead : undefined), ...lift }}
    >
      {index + 1}
    </span>
  );
}

const pin: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  background: SOFT.ink,
  color: SOFT.gold,
  borderRadius: RADIUS.pill,
  fontSize: 12.5,
  fontWeight: 600,
  lineHeight: 1,
  fontVariantNumeric: "tabular-nums",
  boxShadow: "0 2px 6px rgba(17,14,57,0.22)",
};

/** Pointed at: up a little, a touch larger, ringed in white then navy. */
const activeLift: React.CSSProperties = {
  transform: "translateY(-3px) scale(1.12)",
  boxShadow: `0 0 0 2px #fff, 0 0 0 3px ${SOFT.ink}, 0 6px 14px rgba(17,14,57,0.28)`,
};

/** The answer's leading result, in the one colour the chat spends sparingly. */
const pinLead: React.CSSProperties = { background: SOFT.gold, color: LEAD_INK };

/** An unnumbered pin: present, not shouting. */
const dot: React.CSSProperties = {
  display: "block",
  width: 14,
  height: 14,
  borderRadius: RADIUS.pill,
  background: SOFT.ink,
  border: `3px solid ${SOFT.gold}`,
  boxSizing: "border-box",
};
